<template>
  <div class="day-widget">
   
    <!-- Day Level (Always visible) -->
    <div class="day-row fs-sm" @click="toggleDay" :class="{ 'active': isExpanded }">
      <div class="d-flex align-items-center">
        <div class="d-flex align-items-center flex-shrink-0" style="width: 100px;">
          <i class="bi bi-chevron-right me-1" :class="{ 'rotated': isExpanded }"></i>
          <div class="icon me-1">
            <div class="" :class="day.icon"></div>
          </div>
          <div class="overflow-hidden text-nowrap responsive-text">{{ formatDate(day) }}</div>
        </div>

        <div class="flex-grow-1 d-flex gap-3 justify-content-around text-center">
          <div>
            <div class="fs-xxs text-muted">High/Low</div>
            <div>
              <span v-html="formattedValue(day,'tempmax')" />/<span v-html="formattedValue(day,'tempmin')" />
            </div>
          </div>
          <div class="fs-sm">
            <div class="fs-xxs text-muted">Precip</div>
            <div v-if="shouldShowPrecipProb(day)"><i class="bi bi-droplet fs-xxs me-1"></i>{{ formattedValue(day, 'precipprob') }}</div>
            <template v-if="day.precipprob>30 && day.precip">
              <div class="d-flex align-items-center justify-content-center">
                <i class="bi bi-droplet fs-xxs me-1"></i>{{ formattedValue(day, 'precip') }}
              </div>
            </template>

            <div v-if="day.precipremote" class="d-flex align-items-center justify-content-center">
              <i class="bi bi-broadcast fs-xxs   me-1"></i>{{ formattedValue(day, 'precipremote', false, 'precip') }}
            </div>
            <div v-if="day.snow" class="d-flex align-items-center justify-content-center">
              <i class="bi bi-snow fs-xxs me-1"></i>{{ formattedValue(day, 'snow') }}
            </div>

          </div>
          <div>
            <div class="fs-xxs text-muted">Wind</div>
            <div class="d-flex justify-content-center">
              <div class="position-relative" style="width:15px">
                <div class="position-absolute" :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                  <i class="bi bi-arrow-down"></i>
                </div>
              </div>
              <span v-html="formattedValue(day, 'windspeed')" class="d-none d-md-inline-block " />
              <span v-html="formattedValue(day, 'windspeed', true)" class="d-inline-block d-md-none" />
            </div>
            <div class="d-flex justify-content-center fs-xs">
              Gust: <span v-html="formattedValue(day, 'windgust')" class="d-none d-md-inline-block " />
              <span v-html="formattedValue(day, 'windgust', true)" class="d-inline-block d-md-none" />
            </div>
          </div>
          <div class="d-none d-sm-block">
            <div class="fs-xxs text-muted">Visibility</div>
            <div class="" >{{formattedValue(day,'visibility')}}</div>
          </div>
          <div class="d-none d-sm-block">
            <div class="fs-xxs text-muted">AQI</div>
            <div>{{formattedValue(day,'aqi',true)}}</div>
          </div>
          <div class="d-none d-sm-block">
            <div class="fs-xxs text-muted">UV</div>
            <div>{{formattedValue(day,'uvindex',true)}}</div>
          </div>
          
          <!-- More details button -->
          <div>
            <button class="btn btn-sm btn-outline-primary p-1" 
                    @click.stop="showDayDetails = true"
                    title="More details">
              <i class="bi bi-three-dots"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Day Details Modal -->
    <Teleport to="body">
      <div class="vc-bs">
        <!-- Modal Backdrop -->
        <div v-if="showDayDetails" class="modal-backdrop fade" :class="{ show: showDayDetails }" @click="showDayDetails = false"></div>
        
        <!-- Modal -->
        <div v-if="showDayDetails" class="modal fade" :class="{ show: showDayDetails }" style="display: block;" tabindex="-1">
          <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content" @click.stop>
              <div class="modal-header">
                <h5 class="modal-title d-flex align-items-center">
                  <div class="icon me-2">
                    <div class="" :class="day.icon"></div>
                  </div>
                  <span>{{ formatDate(day) }} - Detailed Weather</span>
                </h5>
                <button type="button" class="btn-close" @click="showDayDetails = false"></button>
              </div>
              
              <div class="modal-body">
                <!-- Description -->
                <div v-if="day.description" class="alert alert-info">
                  <i class="bi bi-info-circle me-2"></i>
                  <span v-html="day.description"></span>
                </div>

                <!-- Temperature Details -->
                <div class="card mb-3">
                  <div class="card-header bg-primary text-white">
                    <i class="bi bi-thermometer-half me-2"></i>Temperature
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">High</div>
                        <div class="fw-bold" v-html="formattedValue(day, 'tempmax')"></div>
                        <div v-if="day.feelslikemax !== undefined && day.feelslikemax !== day.tempmax" class="text-muted small">
                          Feels like: <span v-html="formattedValue(day, 'feelslikemax')"></span>
                        </div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Low</div>
                        <div class="fw-bold" v-html="formattedValue(day, 'tempmin')"></div>
                        <div v-if="day.feelslikemin !== undefined && day.feelslikemin !== day.tempmin" class="text-muted small">
                          Feels like: <span v-html="formattedValue(day, 'feelslikemin')"></span>
                        </div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Average</div>
                        <div class="fw-bold" v-html="formattedValue(day, 'temp')"></div>
                        <div v-if="day.feelslike !== undefined" class="text-muted small">
                          Feels like: <span v-html="formattedValue(day, 'feelslike')"></span>
                        </div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Dew Point</div>
                        <div class="fw-bold" v-html="formattedValue(day, 'dew')"></div>
                      </div>
                    </div>
                    
                    <!-- Normal temperatures -->
                    <div v-if="day.normal" class="mt-3 pt-3 border-top">
                      <h6 class="mb-3">Historical Normals</h6>
                      <div class="row g-3">
                        <div class="col-6">
                          <div class="text-muted small mb-2">Normal High</div>
                          <div class="d-flex gap-3 small">
                            <div>
                              <div class="text-muted">Min</div>
                              <div v-html="formatNormalValue(day, 'tempmax', 0)"></div>
                            </div>
                            <div>
                              <div class="text-muted">Avg</div>
                              <div v-html="formatNormalValue(day, 'tempmax', 1)"></div>
                            </div>
                            <div>
                              <div class="text-muted">Max</div>
                              <div v-html="formatNormalValue(day, 'tempmax', 2)"></div>
                            </div>
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="text-muted small mb-2">Normal Low</div>
                          <div class="d-flex gap-3 small">
                            <div>
                              <div class="text-muted">Min</div>
                              <div v-html="formatNormalValue(day, 'tempmin', 0)"></div>
                            </div>
                            <div>
                              <div class="text-muted">Avg</div>
                              <div v-html="formatNormalValue(day, 'tempmin', 1)"></div>
                            </div>
                            <div>
                              <div class="text-muted">Max</div>
                              <div v-html="formatNormalValue(day, 'tempmin', 2)"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Precipitation Details -->
                <div class="card mb-3">
                  <div class="card-header text-white" style="background-color: #17a2b8;">
                    <i class="bi bi-cloud-rain me-2"></i>Precipitation
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6 col-md-4">
                        <div class="text-muted small">Probability</div>
                        <div class="fw-bold">{{ formattedValue(day, 'precipprob') }}</div>
                      </div>
                      <div class="col-6 col-md-4" v-if="day.precip">
                        <div class="text-muted small">Station Amount</div>
                        <div class="fw-bold">{{ formattedValue(day, 'precip') }}</div>
                      </div>
                      <div class="col-6 col-md-4" v-if="day.precipremote">
                        <div class="text-muted small">Remote Precip</div>
                        <div class="fw-bold">{{ formattedValue(day, 'precipremote') }}</div>
                      </div>
                      <div class="col-6 col-md-4" v-if="day.snow">
                        <div class="text-muted small">Snow</div>
                        <div class="fw-bold">{{ formattedValue(day, 'snow') }}</div>
                      </div>
                      <div class="col-6 col-md-4" v-if="day.snowdepth">
                        <div class="text-muted small">Snow Depth</div>
                        <div class="fw-bold">{{ formattedValue(day, 'snowdepth') }}</div>
                      </div>
                      <div class="col-6 col-md-4" v-if="day.preciptype && day.preciptype.length">
                        <div class="text-muted small">Type</div>
                        <div class="fw-bold">{{ day.preciptype.join(', ') }}</div>
                      </div>
                      <div class="col-6 col-md-4">
                        <div class="text-muted small">Humidity</div>
                        <div class="fw-bold">{{ formattedValue(day, 'humidity') }}</div>
                      </div>
                      <div class="col-6 col-md-4 " v-if="day.cloudcover !== undefined">
                        <div class="text-muted small">Cloud Cover</div>
                        <div class="fw-bold">{{ formattedValue(day, 'cloudcover') }}</div>
                      </div>

                    </div>
                    
                  </div>
                </div>

                <!-- Wind Details -->
                <div class="card mb-3">
                  <div class="card-header bg-success text-white">
                    <i class="bi bi-wind me-2"></i>Wind
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Speed</div>
                        <div class="fw-bold">{{ formattedValue(day, 'windspeed') }}</div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Direction</div>
                        <div class="fw-bold d-flex align-items-center">
                          <div class="position-relative me-2" style="width:20px; height:20px;">
                            <div class="position-absolute top-0 start-0" :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                              <i class="bi bi-arrow-down"></i>
                            </div>
                          </div>
                          <span>{{ formattedValue(day, 'wind') }}</span>
                        </div>
                      </div>
                      <div class="col-6 col-md-3" v-if="day.windgust">
                        <div class="text-muted small">Gust</div>
                        <div class="fw-bold">{{ formattedValue(day, 'windgust') }}</div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Pressure</div>
                        <div class="fw-bold">{{ formattedValue(day, 'pressure') }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Sun & Moon -->
                <div class="card mb-3">
                  <div class="card-header bg-warning">
                    <i class="bi bi-sun me-2"></i>Sun & Moon
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Sunrise</div>
                        <div class="fw-bold">
                          <i class="bi bi-sunrise text-warning me-1"></i>
                          {{ formatTime(day, 'sunriseEpoch') }}
                        </div>
                      </div>
                      <div class="col-6 col-md-3">
                        <div class="text-muted small">Sunset</div>
                        <div class="fw-bold">
                          <i class="bi bi-sunset text-warning me-1"></i>
                          {{ formatTime(day, 'sunsetEpoch') }}
                        </div>
                      </div>
                      <div class="col-6 col-md-3" v-if="day.moonphase !== undefined">
                        <div class="text-muted small">Moon Phase</div>
                        <div class="fw-bold">{{ formattedValue(day, 'moonphase') }}</div>
                      </div>
                      <div class="col-6 col-md-3" v-if="day.uvindex !== undefined">
                        <div class="text-muted small">UV Index</div>
                        <div class="fw-bold">{{ formattedValue(day, 'uvindex') }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Air Quality -->
                <div class="card mb-3" v-if="day.aqius">
                  <div class="card-header bg-light text-dark">
                    <i class="bi bi-lungs me-2"></i>Air Quality
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-6">
                        <div class="text-muted small">AQI</div>
                        <div class="fw-bold d-flex align-items-center">
                          {{ formattedValue(day, 'aqi') }}
                          <span class="badge ms-2" 
                                :class="getAqiBadgeClass(day.aqius)">
                            {{ formattedAqi(day.aqius) }}
                          </span>
                        </div>
                      </div>
                      <div class="col-6" v-if="day.aqielement">
                        <div class="text-muted small">Primary Pollutant</div>
                        <div class="fw-bold">{{ formattedValue(day, 'aqielement') }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Other Conditions -->
                <div class="card mb-3">
                  <div class="card-header bg-dark text-white">
                    <i class="bi bi-list-ul me-2"></i>Other Conditions
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      
                      <div class="col-6 col-md-4 col-lg-3" v-if="day.solarradiation">
                        <div class="text-muted small">Solar Radiation</div>
                        <div class="fw-bold">{{ formattedValue(day, 'solarradiation') }}</div>
                      </div>
                      <div class="col-6 col-md-4 col-lg-3" v-if="day.solarenergy">
                        <div class="text-muted small">Solar Energy</div>
                        <div class="fw-bold">{{ formattedValue(day, 'solarenergy') }}</div>
                      </div>
                      <div class="col-6 col-md-4 col-lg-3">
                        <div class="text-muted small">Visibility</div>
                        <div class="fw-bold">{{ formattedValue(day, 'visibility') }}</div>
                      </div>
                      <div class="col-12" v-if="day.conditions">
                        <div class="text-muted small">Conditions</div>
                        <div class="fw-bold">{{ day.conditions }}</div>
                      </div>

                    </div>
                  </div>
                </div>

                <!-- Stations -->
                <div class="card" v-if="day.stations && day.stations.length">
                  <div class="card-header">
                    <i class="bi bi-broadcast-pin me-2"></i>Data Sources
                  </div>
                  <div class="card-body">
                    <div class="small text-muted">{{ day.stations.join(', ') }}</div>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="showDayDetails = false">Close</button>
              </div>
            </div>
          </div>
        </div>
        </div>
    </Teleport>

    <!-- Hour Level (Shown when day is expanded) -->
    <transition name="slide">
      <div v-if="isExpanded" class="hours-container ">
        <div v-for="(hour, hourIndex) in day.hours" :key="hourIndex">
          <!-- Hour Row -->
          <div class="hour-row"
               @click="toggleHour(hourIndex)"
               :class="{ 'active': expandedHour === hourIndex }">
            
            <div class="d-flex align-items-center fs-xs" style="max-width:800px">
              <div class="d-flex align-items-center flex-shrink-0" style="width: 120px;">
                <i class="bi bi-chevron-right ms-2 me-2" :class="{ 'rotated': expandedHour === hourIndex }"></i>
                <div class="icon icon-small me-2">
                  <div class="" :class="hour.icon"></div>
                </div>
                <div class="overflow-hidden text-nowrap responsive-text">{{ formatTime(hour) }}</div>
              </div>

              <div class="flex-grow-1 d-flex gap-3 justify-content-around text-center">
                <div>
                  <div class="fs-xxs text-muted">Temp</div>
                  <span v-html="formattedValue(hour,'temp')" />
                  <div v-if="hour.feelslike !== hour.temp" class="fs-xxs text-muted">
                    (<span v-html="formattedValue(hour,'feelslike')" />)
                  </div>

                </div>

                <div class="fs-sm">
                  <div class="fs-xxs text-muted">Precip</div>
                  <template v-if="shouldShowPrecipProb(hour)" >
                  <div class="d-flex align-items-center justify-content-center">
                    <i class="bi bi-droplet fs-xxs me-1"></i>{{ formattedValue(hour, 'precipprob') }}
                  </div>
                  <div v-if="hour.precip" class="d-flex align-items-center justify-content-center">
                    <i class="bi bi-droplet fs-xxs me-1"></i>{{ formattedValue(hour, 'precip') }}
                  </div>
                  <div v-if="hour.precipremote" class="d-flex align-items-center justify-content-center">
                    <i class="bi bi-broadcast fs-xxs me-1"></i>{{ formattedValue(hour, 'precipremote', false, 'precip') }}
                  </div>
                  <div v-if="hour.snow" class="d-flex align-items-center justify-content-center">
                    <i class="bi bi-snow fs-xxs me-1"></i>{{ formattedValue(hour, 'snow') }}
                  </div>
                  </template>
                  <div v-else> - </div>
                </div>

                <div>
                  <div class="fs-xxs text-muted">Wind</div>
                  <div class="d-flex justify-content-center">
                    <div class="position-relative" style="width:15px">
                      <div class="position-absolute" :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                        <i class="bi bi-arrow-down small"></i>
                      </div>
                    </div>
                    <span v-html="formattedValue(hour, 'windspeed', false)" />
                  </div>
                </div>
                <div class="">
                  <div class="fs-xxs text-muted">Visibility</div>
                  <div class="" >{{formattedValue(day,'visibility')}}</div>
                </div>
                <div class="d-none d-sm-block">
                  <div class="fs-xxs text-muted">AQI</div>
                  <div>{{formattedValue(day,'aqi',true)}}</div>
                </div>
                <div class="d-none d-sm-block">
                  <div class="fs-xxs text-muted">UV</div>
                  <div>{{formattedValue(day,'uvindex',true)}}</div>
                </div>
                <!--
                <div class="d-none d-sm-block">
                  <div v-html="formattedValue(hour, 'uvindex')"></div>
                </div>
                -->
              </div>
            </div>
          </div>
          
          <!-- Minute Level (Shown when this specific hour is expanded) -->
          <transition name="slide">
            <div v-if="expandedHour === hourIndex && hour.minutes" 
                 class="minutes-container">
                 
              <div v-for="(minute, minuteIndex) in hour.minutes"
                   :key="minuteIndex"
                   class="minute-row">
                
                <div class="d-flex align-items-center">
                  <div class="flex-shrink-0 text-center" style="width: 100px;">
                    <div class="overflow-hidden text-nowrap responsive-text">{{ formatTime(minute) }}</div>
                  </div>

                  <div class="flex-grow-1 d-flex gap-3 justify-content-around text-center fs-sm">
                    <div>
                      <div class="fs-xxs text-muted">Temp</div>
                      <span v-html="formattedValue(minute,'temp')" />
                    </div>
                    <div>
                      <div class="fs-xxs text-muted">Precip</div>
                      <div v-if="minute.precip">{{ formattedValue(minute, 'precip') }}</div>
                      <div v-if="minute.reflectivity" class="fs-xxs">
                        {{ formattedValue(minute, "reflectivity")}} dBZ
                      </div>
                     
                    </div>
                    <div>
                      <div class="fs-xxs text-muted">Wind</div>
                      <div class="d-flex justify-content-center">
                        <div class="position-relative" style="width:12px">
                          <div class="position-absolute" :style="'transform:rotate(' + Math.round(minute.winddir) + 'deg)'">
                            <i class="bi bi-arrow-down" style="font-size: 0.7rem;"></i>
                          </div>
                        </div>
                        <span v-html="formattedValue(minute, 'windspeed', false)" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { VcWidgetHelper } from './common_widget.js'

export default {
  name: 'daywidget',
  props: {
    days: Object,
    day: Object,
    unitGroup: String,
    timezone: String,
    isSelected: Boolean
  },
  data() {
    return {
      expandedHour: null,
      showDayDetails: false,
    }
  },
  computed: {
    isExpanded() {
      return this.day && this.day.isExpanded;
    }
  },
  methods: {
    toggleDay() {
      // Close all other days
      this.days.forEach(d => {
        d.isExpanded = (d === this.day) ? !d.isExpanded : false;
      });
      
      // Reset hour expansion when closing day
      if (!this.day.isExpanded) {
        this.expandedHour = null;
      }
    },
    
    toggleHour(hourIndex) {
      // Toggle hour expansion
      this.expandedHour = this.expandedHour === hourIndex ? null : hourIndex;
    },
    
    formatNormalValue(period, element, index) {
      let me = this;
      if (!period || !period.normal) return "?";
      let normals;
      if (element === "rainsnow") {
        if (period.snow > 0) {
          normals = period.normal["snow"];
        } else {
          normals = period.normal["precip"];
        }
      } else {
        normals = period.normal[element];
      }

      return VcWidgetHelper.formatElementValue(normals[index], element, me.unitGroup, null, "", false, true);
    },
    formattedValue(period, element, shortForm, formatElement) {
        var me=this;
        if (period===null || period===undefined) return "-";
        formatElement=formatElement || element;
        if (element==="rainsnow") {
          if (period.snow>0) return me.formatValue(period.snow, "snow");
          return me.formatValue(period.precip, "precip");
        }
        if (element==="aqi") return me.formattedAqi(period,shortForm);
        if (element==="aqielement") return me.formattedAqiElement(period);
        if (element==="wind") return me.formattedWind(period);
        if (element==="moonphase") return me.formattedMoonPhase(period);
        if (element==="sunriseset") return me.formattedSunriseset(period);
        return me.formatValue(period[element], formatElement, shortForm);
    },
    /*
    formatteddBZ(value,shortForm) {
      if (!value && value!==0) return "-";
      return `${value} dBZ`
    },
    */
    formattedAqi(period,shortForm) {
      let aqi=period.aqius;
      if (!aqi && aqi!==0) return "-";
      //return `${VcWidgetHelper.getAqiText(aqi,shortForm)} (${this.formatValue(aqi, "aqius")})`
      return `${VcWidgetHelper.getAqiText(aqi,shortForm)}`
    },
    formattedAqiElement(period) {
      let aqielement=period.aqielement;
      let aqi=period.aqius;
      if (!aqielement) return  `(${this.formatValue(aqi, "aqius")})`;
      return `${VcWidgetHelper.getAqiElementText(aqielement)} - ${this.formatValue(aqi, "aqius")}`;
    },
    formattedWind(period) {
      return VcWidgetHelper.getCompassDirection(period.winddir) + " " +
        this.formatValue(period.windspeed, "wind");
    },
    formattedMoonPhase(period) {
      return VcWidgetHelper.getMoonPhaseDesc(period.moonphase);
    },
    formattedSunriseset(period) {
      return this.formatValue(period.sunrise) + "-" + this.formatValue(period.sunset);
    },
    formatValue(value, element, shortForm) {
      var me = this;
      const fixed = shortForm ? 0 : null;
      const emptyvalue = null, unicode = null;
      const noSymbol = shortForm;
      if (element=='precipremote') {
        element='precip';
      }
      return VcWidgetHelper.formatElementValue(value, element, me.unitGroup, fixed, emptyvalue, unicode, noSymbol);
    },
    formatTime(period, property) {
      if (!period) return "-";
      const value = period && property && period[property] ? period[property] : period.datetimeEpoch;
      return VcWidgetHelper.formatTime(value, this.timezone);
    },
    formatDate(period) {
      if (!period) return "-";
      if (VcWidgetHelper.isYesterday(period.datetimeEpoch, this.timezone)) return "Yesterday";
      if (VcWidgetHelper.isToday(period.datetimeEpoch, this.timezone)) return "Today";
      if (VcWidgetHelper.isNextNDays(5, period.datetimeEpoch, this.timezone)) {
        return VcWidgetHelper.formatDateDayOfWeek(period.datetimeEpoch, this.timezone);
      }


      //if (shortForm) return VcWidgetHelper.formatDateVeryShort(period.datetimeEpoch, this.timezone);
      return VcWidgetHelper.formatDateMonth(period.datetimeEpoch, this.timezone);
    },
    shouldShowPrecipProb(period) {
      //return true;
      if (!period) return false;
      const now = Date.now() / 1000;
      const periodTime = period.datetimeEpoch;
      if (!periodTime) return false;

      if (VcWidgetHelper.isToday(periodTime, this.timezone)) {
        if (period.precipremote && period.precipremote > 0) {
          return false;
        }
        return true;
      }

      if (periodTime < now) {
        return false;
      }
      return true;
      /*
      const hasPrecip = (period.precip && period.precip > 0) || 
                       (period.precipremote && period.precipremote > 0) ||
                       (period.snow && period.snow > 0);
      
      return !hasPrecip;
      */
    },
    
    getAqiBadgeClass(aqi) {
      if (aqi <= 50) return 'bg-success';
      if (aqi <= 100) return 'bg-warning text-dark';
      if (aqi <= 150) return 'bg-orange text-white';
      if (aqi <= 200) return 'bg-danger';
      if (aqi <= 300) return 'bg-purple text-white';
      return 'bg-maroon text-white';
    },
    
    // ... rest of existing methods ...
  }
}
</script>

<style scoped>
.day-widget {
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.5rem;
  background: var(--bs-light);
}

.day-row,
.hour-row,
.minute-row {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--bs-gray-300);
}

.day-row:hover,
.hour-row:hover {
  background-color: var(--bs-gray-200);
}

.day-row.active,
.hour-row.active {
  background-color: var(--bs-gray-300);
}

.hour-row {
  background-color: var(--bs-gray-100);
  border-left: 3px solid var(--bs-primary);
}

.minute-row {
  background-color: var(--bs-white);
  border-left: 6px solid var(--bs-info);
  padding: 0.5rem 1rem;
  cursor: default;
}

.minute-row:hover {
  background-color: var(--bs-gray-50);
}

/* Chevron animation */
.bi-chevron-right {
  transition: transform 0.3s;
  font-size: 0.8rem;
}

.bi-chevron-right.rotated {
  transform: rotate(90deg);
}

/* Icon sizes */
.icon-small {
  font-size: 1.2rem;
}

.icon-small > div {
  font-size: inherit;
}

/* Slide transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 2000px;
  opacity: 1;
}



/* Badge colors */
.bg-orange {
  background-color: #ff8c00 !important;
}

.bg-purple {
  background-color: #800080 !important;
}

.bg-maroon {
  background-color: #800000 !important;
}

/* Three dots button */
.btn-outline-primary {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
}

.btn-outline-primary:hover {
  background-color: var(--bs-primary);
  color: white;
}


/* Mobile optimizations */
@media (max-width: 576px) {
  .day-row,
  .hour-row {
    padding: 0.5rem;
  }
  
  .minute-row {
    padding: 0.4rem 0.5rem;
  }
  
  .fs-xxs {
    font-size: 0.65rem !important;
  }
  
  .icon {
    font-size: 1.5rem;
  }
  
  .icon-small {
    font-size: 1rem;
  }
}

/* Ensure text doesn't wrap */
.text-center > div {
  white-space: nowrap;
}
.responsive-text {
  font-size: clamp(0.75rem, 1vw, 0.875rem);
}
</style>
